import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Table,
  Space,
  Input,
  Typography,
  Tag,
  Alert,
  Spin,
  Tooltip,
  Badge,
  Divider,
  Form,
  Select,
  Tabs,
  Timeline,
  Card
} from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  HistoryOutlined,
  BugOutlined,
  ToolOutlined,
  RedoOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useStuckWithdrawalTransactions,
  useManuallyConfirmTransaction,
  useResetTransaction,
  useForceStateChange,
  useRetryCallback
} from '../../hooks/useManualIntervention';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface ManualInterventionModalProps {
  visible: boolean;
  onClose: () => void;
}

const ManualInterventionModal: React.FC<ManualInterventionModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Broadcast');
  const [olderThanMinutes, setOlderThanMinutes] = useState(30);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [forceStateModalVisible, setForceStateModalVisible] = useState(false);
  const [retryCallbackModalVisible, setRetryCallbackModalVisible] = useState(false);
  const [targetState, setTargetState] = useState('');
  const [showManualInterventionOnly, setShowManualInterventionOnly] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('details');

  // Fetch stuck transactions
  const {
    data: stuckTransactionsData,
    isLoading,
    isError,
    error,
    refetch
  } = useStuckWithdrawalTransactions({
    olderThanMinutes,
    status: selectedStatus,
    requiresManualIntervention: showManualInterventionOnly,
    enabled: visible
  });

  // Manual intervention mutations
  const manuallyConfirmMutation = useManuallyConfirmTransaction();
  const resetTransactionMutation = useResetTransaction();
  const forceStateChangeMutation = useForceStateChange();
  const retryCallbackMutation = useRetryCallback();

  // Reset state when modal is opened
  useEffect(() => {
    if (visible) {
      setSearchText('');
      setSelectedTransaction(null);
      setAdminNotes('');
      refetch();
    }
  }, [visible, refetch]);

  // Filter transactions based on search text
  const filteredTransactions = stuckTransactionsData?.data
    ? stuckTransactionsData.data.filter((tx: any) => {
        if (!searchText) return true;

        const searchLower = searchText.toLowerCase();
        return (
          (tx._id && tx._id.toLowerCase().includes(searchLower)) ||
          (tx.txid && tx.txid.toLowerCase().includes(searchLower)) ||
          (tx.to_address && tx.to_address.toLowerCase().includes(searchLower)) ||
          (tx.from_address && tx.from_address.toLowerCase().includes(searchLower)) ||
          (tx.network && tx.network.toLowerCase().includes(searchLower)) ||
          (tx.coin && tx.coin.toLowerCase().includes(searchLower))
        );
      })
    : [];

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Calculate time stuck
  const calculateTimeStuck = (dateString: string) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins} minutes`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours} hours ${mins} minutes`;
    } else {
      const days = Math.floor(diffMins / 1440);
      const hours = Math.floor((diffMins % 1440) / 60);
      return `${days} days ${hours} hours`;
    }
  };

  // Handle manual confirmation
  const handleManualConfirmation = () => {
    if (!selectedTransaction) return;

    manuallyConfirmMutation.mutate(
      {
        transactionId: selectedTransaction._id,
        adminNotes: adminNotes || 'Manually confirmed by admin'
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            setConfirmModalVisible(false);
            refetch();
          }
        }
      }
    );
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      render: (text: string) => (
        <Tooltip title={text}>
          <Text copyable={{ text }} style={{ maxWidth: 150 }} ellipsis>
            {text}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'TXID',
      dataIndex: 'txid',
      key: 'txid',
      render: (text: string) => (
        text ? (
          <Tooltip title={text}>
            <Text copyable={{ text }} style={{ maxWidth: 150 }} ellipsis>
              {text}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">N/A</Text>
        )
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text: string) => {
        let color = 'default';
        let icon = null;

        switch (text) {
          case 'Broadcast':
            color = 'processing';
            icon = <ClockCircleOutlined />;
            break;
          case 'Confirmed':
            color = 'success';
            icon = <CheckCircleOutlined />;
            break;
          case 'Failed':
            color = 'error';
            icon = <ExclamationCircleOutlined />;
            break;
          default:
            icon = <InfoCircleOutlined />;
        }

        return <Tag color={color} icon={icon}>{text}</Tag>;
      }
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text: string, record: any) => (
        <Text>
          {text} {record.coin}
        </Text>
      )
    },
    {
      title: 'Network',
      dataIndex: 'network',
      key: 'network'
    },
    {
      title: 'Queued At',
      dataIndex: 'queued_at',
      key: 'queued_at',
      render: (text: string) => formatDate(text)
    },
    {
      title: 'Time Stuck',
      dataIndex: 'queued_at',
      key: 'time_stuck',
      render: (text: string) => {
        const timeStuck = calculateTimeStuck(text);
        let color = 'green';

        if (timeStuck.includes('hours')) {
          color = 'orange';
        }
        if (timeStuck.includes('days')) {
          color = 'red';
        }

        return <Text style={{ color }}>{timeStuck}</Text>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          type="primary"
          onClick={() => {
            setSelectedTransaction(record);
            setConfirmModalVisible(true);
          }}
        >
          Manually Confirm
        </Button>
      )
    }
  ];

  return (
    <>
      <Modal
        title={t('queue:manualIntervention.title', 'Manual Intervention for Stuck Transactions')}
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={[
          <Button key="close" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message={t('queue:manualIntervention.warning', 'Warning')}
            description={t(
              'queue:manualIntervention.warningDescription',
              'Manual intervention should only be used when transactions are stuck and cannot be processed normally. Always verify transaction status on the blockchain before confirming.'
            )}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Space>
              <Select
                value={selectedStatus}
                onChange={setSelectedStatus}
                style={{ width: 150 }}
              >
                <Option value="Broadcast">Broadcast</Option>
                <Option value="Requested">Requested</Option>
                <Option value="Approved">Approved</Option>
              </Select>

              <Select
                value={olderThanMinutes}
                onChange={setOlderThanMinutes}
                style={{ width: 150 }}
              >
                <Option value={15}>Older than 15 minutes</Option>
                <Option value={30}>Older than 30 minutes</Option>
                <Option value={60}>Older than 1 hour</Option>
                <Option value={180}>Older than 3 hours</Option>
                <Option value={360}>Older than 6 hours</Option>
                <Option value={720}>Older than 12 hours</Option>
                <Option value={1440}>Older than 1 day</Option>
              </Select>

              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                loading={isLoading}
              >
                {t('common:actions.refresh')}
              </Button>
            </Space>

            <Input
              placeholder={t('queue:manualIntervention.search', 'Search by ID, TXID, address...')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
              allowClear
            />
          </div>

          {isError && (
            <Alert
              message={t('queue:manualIntervention.error', 'Error')}
              description={error?.toString() || t('queue:manualIntervention.unknownError', 'An unknown error occurred')}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Badge
                count={filteredTransactions.length}
                style={{ backgroundColor: filteredTransactions.length > 0 ? '#ff4d4f' : '#52c41a' }}
              >
                <Title level={5} style={{ margin: 0 }}>
                  {t('queue:manualIntervention.stuckTransactions', 'Stuck Transactions')}
                </Title>
              </Badge>

              <Table
                columns={columns}
                dataSource={filteredTransactions}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: t('queue:manualIntervention.noTransactions', 'No stuck transactions found')
                }}
              />
            </>
          )}
        </Space>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title={t('queue:manualIntervention.confirmTitle', 'Confirm Manual Intervention')}
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmModalVisible(false)}>
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleManualConfirmation}
            loading={manuallyConfirmMutation.isPending}
          >
            {t('queue:manualIntervention.confirm', 'Confirm Transaction')}
          </Button>
        ]}
      >
        {selectedTransaction && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message={t('queue:manualIntervention.confirmWarning', 'Warning')}
              description={t(
                'queue:manualIntervention.confirmWarningDescription',
                'You are about to manually confirm this transaction. This action cannot be undone. Make sure you have verified the transaction status on the blockchain.'
              )}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <div>
              <Text strong>{t('queue:manualIntervention.transactionId', 'Transaction ID')}:</Text>
              <Text copyable={{ text: selectedTransaction._id }}> {selectedTransaction._id}</Text>
            </div>

            {selectedTransaction.txid && (
              <div>
                <Text strong>{t('queue:manualIntervention.txid', 'TXID')}:</Text>
                <Text copyable={{ text: selectedTransaction.txid }}> {selectedTransaction.txid}</Text>
              </div>
            )}

            <div>
              <Text strong>{t('queue:manualIntervention.status', 'Status')}:</Text>
              <Text> {selectedTransaction.status}</Text>
            </div>

            <div>
              <Text strong>{t('queue:manualIntervention.amount', 'Amount')}:</Text>
              <Text> {selectedTransaction.amount} {selectedTransaction.coin}</Text>
            </div>

            <div>
              <Text strong>{t('queue:manualIntervention.network', 'Network')}:</Text>
              <Text> {selectedTransaction.network}</Text>
            </div>

            <div>
              <Text strong>{t('queue:manualIntervention.toAddress', 'To Address')}:</Text>
              <Text copyable={{ text: selectedTransaction.to_address }}> {selectedTransaction.to_address}</Text>
            </div>

            <Divider />

            <Form layout="vertical">
              <Form.Item
                label={t('queue:manualIntervention.adminNotes', 'Admin Notes')}
                required
              >
                <TextArea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={t('queue:manualIntervention.adminNotesPlaceholder', 'Enter reason for manual confirmation...')}
                  rows={4}
                />
              </Form.Item>
            </Form>

            {manuallyConfirmMutation.isError && (
              <Alert
                message={t('queue:manualIntervention.error', 'Error')}
                description={manuallyConfirmMutation.error?.toString() || t('queue:manualIntervention.unknownError', 'An unknown error occurred')}
                type="error"
                showIcon
              />
            )}
          </Space>
        )}
      </Modal>
    </>
  );
};

export default ManualInterventionModal;
